import { useEffect, useState, useCallback } from 'react';
import { useToast } from '../../hooks/useToast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import api from '../../services/api';
import ListLoadError from '../../components/admin/ListLoadError';
import styles from './CSVReceivedPage.module.css';

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const SOURCE_COLORS = {
  E123: { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  FirstEnroll: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
};

const getSourceStyle = (source) =>
  SOURCE_COLORS[source] || { bg: '#f5f5f5', color: '#525252', border: '#d4d4d4' };

const STATUS_MAP = {
  pending: { label: 'Pending', className: 'statusPending' },
  imported: { label: 'Imported', className: 'statusImported' },
  error: { label: 'Error', className: 'statusError' },
};

const CSVReceivedPage = () => {
  const { toast } = useToast();
  const [csvList, setCsvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [importing, setImporting] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [resultModal, setResultModal] = useState(null);
  const [viewModal, setViewModal] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const loadCSVs = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await api.get('/admin/csv-received');
      setCsvList(response.csvReceived || []);
    } catch {
      setLoadError(true);
      setCsvList([]);
      toast.error('Failed to load CSVs');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadCSVs();
  }, [loadCSVs]);

  const handleView = async (csv) => {
    setViewLoading(true);
    setViewModal({ filename: csv.filename, rows: [], headers: [] });
    try {
      const response = await api.get(`/admin/csv-received/${csv.id}`);
      const fileData = response.csvReceived?.fileData || '';
      const lines = fileData.split('\n').filter((l) => l.trim());
      if (lines.length === 0) {
        setViewModal((prev) => ({ ...prev, rows: [], headers: [] }));
        return;
      }
      const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
      const rows = lines.slice(1).map((line) => {
        const vals = [];
        let current = '';
        let inQuotes = false;
        for (const ch of line) {
          if (ch === '"') { inQuotes = !inQuotes; }
          else if (ch === ',' && !inQuotes) { vals.push(current.trim()); current = ''; }
          else { current += ch; }
        }
        vals.push(current.trim());
        return vals;
      });
      setViewModal({ filename: csv.filename, headers, rows });
    } catch {
      toast.error('Failed to load CSV content');
      setViewModal(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleDownload = async (csv) => {
    try {
      const response = await api.get(`/admin/csv-received/${csv.id}`);
      const fileData = response.csvReceived?.fileData || '';
      const blob = new Blob([fileData], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = csv.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download CSV');
    }
  };

  const handleImportClick = (csv) => {
    setConfirmModal(csv);
  };

  const handleImportConfirm = async () => {
    const csv = confirmModal;
    setConfirmModal(null);
    setImporting(csv.id);
    try {
      const response = await api.post(`/admin/csv-received/${csv.id}/import`);
      setResultModal({
        filename: csv.filename,
        importedCount: response.importedCount || 0,
        errorCount: response.errorCount || 0,
        errors: response.errors || [],
      });
      await loadCSVs();
    } catch (err) {
      toast.error(err.message || 'Import failed');
    } finally {
      setImporting(null);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>CSVs Received</h1>
          <p>CSV files received via SFTP sync and manual upload</p>
        </div>
      </div>

      <Card className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>
            <LoadingSpinner />
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Filename</th>
                <th>Source</th>
                <th>Rows</th>
                <th>Status</th>
                <th>Received</th>
                <th>Imported</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {csvList.map((csv) => {
                const srcStyle = getSourceStyle(csv.source);
                const statusInfo = STATUS_MAP[csv.status] || STATUS_MAP.pending;
                return (
                  <tr key={csv.id}>
                    <td>
                      <div className={styles.filename}>{csv.filename}</div>
                    </td>
                    <td>
                      <span
                        className={styles.sourceBadge}
                        style={{ backgroundColor: srcStyle.bg, color: srcStyle.color, borderColor: srcStyle.border }}
                      >
                        {csv.source}
                      </span>
                    </td>
                    <td>
                      <span className={styles.rowCount}>
                        {csv.rowCount?.toLocaleString() || 0}
                      </span>
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[statusInfo.className]}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td>
                      <span className={styles.dateText}>{formatDate(csv.receivedAt)}</span>
                    </td>
                    <td>
                      <span className={styles.dateText}>{formatDate(csv.importedAt)}</span>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button className={styles.actionBtn} onClick={() => handleView(csv)}>
                          View
                        </button>
                        <button className={styles.actionBtn} onClick={() => handleDownload(csv)}>
                          Download
                        </button>
                        {csv.status === 'pending' && (
                          <button
                            className={`${styles.actionBtn} ${styles.importBtn}`}
                            onClick={() => handleImportClick(csv)}
                            disabled={importing === csv.id}
                          >
                            {importing === csv.id ? 'Importing...' : 'Import'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && loadError && (
          <ListLoadError message="Could not load received CSVs" onRetry={loadCSVs} />
        )}

        {!loading && !loadError && csvList.length === 0 && (
          <div className={styles.empty}>No CSVs received yet</div>
        )}
      </Card>

      {/* Confirm Import Modal */}
      <Modal
        isOpen={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        title="Confirm Import"
        size="small"
      >
        {confirmModal && (
          <div className={styles.confirmContent}>
            <p>
              Import <strong>{confirmModal.filename}</strong>?
            </p>
            <p className={styles.confirmSub}>
              This will create/update {confirmModal.rowCount} user records.
            </p>
            <div className={styles.confirmActions}>
              <Button variant="outline" onClick={() => setConfirmModal(null)}>
                Cancel
              </Button>
              <Button onClick={handleImportConfirm}>
                Confirm Import
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Import Results Modal */}
      <Modal
        isOpen={!!resultModal}
        onClose={() => setResultModal(null)}
        title="Import Results"
        size="medium"
      >
        {resultModal && (
          <div className={styles.resultContent}>
            <h3 className={styles.resultFilename}>{resultModal.filename}</h3>
            <div className={styles.resultStats}>
              <div className={styles.resultStat}>
                <span className={styles.resultStatValue}>{resultModal.importedCount}</span>
                <span className={styles.resultStatLabel}>Imported</span>
              </div>
              <div className={`${styles.resultStat} ${resultModal.errorCount > 0 ? styles.resultStatError : ''}`}>
                <span className={styles.resultStatValue}>{resultModal.errorCount}</span>
                <span className={styles.resultStatLabel}>Errors</span>
              </div>
            </div>
            {resultModal.errors.length > 0 && (
              <details className={styles.errorDetails}>
                <summary className={styles.errorSummary}>
                  View {resultModal.errors.length} error(s)
                </summary>
                <div className={styles.errorList}>
                  {resultModal.errors.map((err, i) => (
                    <div key={i} className={styles.errorItem}>
                      <span className={styles.errorRow}>Row {err.row}</span>
                      <span className={styles.errorMsg}>{err.message}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
            <div className={styles.resultActions}>
              <Button onClick={() => setResultModal(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* CSV Preview Modal */}
      <Modal
        isOpen={!!viewModal}
        onClose={() => setViewModal(null)}
        title={viewModal?.filename || 'CSV Preview'}
        size="large"
      >
        {viewModal && (
          <div className={styles.previewContent}>
            {viewLoading ? (
              <div className={styles.loading}>
                <LoadingSpinner />
              </div>
            ) : viewModal.headers.length === 0 ? (
              <div className={styles.empty}>No data in CSV</div>
            ) : (
              <div className={styles.previewTableWrap}>
                <table className={styles.previewTable}>
                  <thead>
                    <tr>
                      {viewModal.headers.map((h, i) => (
                        <th key={i}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {viewModal.rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td key={ci}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CSVReceivedPage;
